import { useEffect, useState } from 'react';

import { TextInput } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import { useUpdatePatientMutation } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { capitalize } from '@/utils/constants';

import ReferringProviderSelectionModal from '@/pages/physician/PatientProfilePage/components/PatientTabs/PatientOverviewTab/components/PatientEditDetailsModal/components/ReferringProviderSelectionModal';
import { getReferringProviderName } from '@/pages/physician/PatientProfilePage/components/PatientTabs/PatientOverviewTab/constant';
import { RegisteredNpi } from '@/types/npi';
import LabeledField from '@/ui/LabeledField';

import styles from '../CareTeamDetails.module.scss';

type ImplantingProviderType = 'NPI_SEARCH' | 'FREE_TEXT';

const ImplantingEPSelect = ({
  patientId,
  implantingEp,
  implantingEpFreeText,
  implantingProviderType
}: {
  patientId: string;
  implantingEp: RegisteredNpi | null;
  implantingProviderType: ImplantingProviderType;
  implantingEpFreeText: string;
}) => {
  const [updatePatient, { isLoading: isUpdatingPatient }] = useUpdatePatientMutation();

  const [opened, { toggle: toggleImplantingEPModal }] = useDisclosure(false);
  const [isEditingFreeText, setIsEditingFreeText] = useState(false);

  const isEHRIntegrated = true;

  const [providerName, setProviderName] = useState<string>('');
  const [freeTextValue, setFreeTextValue] = useState<string>('');

  useEffect(() => {
    if (implantingProviderType === 'FREE_TEXT') {
      const value =
        implantingEpFreeText ||
        getReferringProviderName({ referringProvider: implantingEp, isEHRIntegrated });
      setProviderName(value);
      setFreeTextValue(value);
    } else {
      const value = getReferringProviderName({ referringProvider: implantingEp, isEHRIntegrated });
      setProviderName(value);
    }
  }, [implantingProviderType, implantingEpFreeText, implantingEp]);

  const handleEditClick = () => {
    if (implantingProviderType === 'FREE_TEXT') {
      setIsEditingFreeText(true);
    } else {
      toggleImplantingEPModal();
    }
  };

  const handleImplantingEPChange = async (provider: RegisteredNpi | null) => {
    try {
      await updatePatient({
        patient: {
          id: patientId,
          implantingEp: provider
            ? ({ NPI: provider.NPI } as RegisteredNpi)
            : ({ NPI: null } as unknown as RegisteredNpi)
        }
      }).unwrap();

      notifications.show({
        title: 'Success',
        message: 'Implanting EP updated successfully',
        color: 'green'
      });
    } catch (error) {
      console.error('Error updating Implanting EP:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update Implanting EP. Please try again.',
        color: 'red'
      });
    }
  };

  const handleProviderSelect = (newImplantingEP: RegisteredNpi) => {
    handleImplantingEPChange(newImplantingEP);
    setProviderName(
      getReferringProviderName({ referringProvider: newImplantingEP, isEHRIntegrated })
    );
  };

  const handleFreeTextSave = async () => {
    setIsEditingFreeText(false);
    setProviderName(freeTextValue);

    try {
      await updatePatient({
        patient: {
          id: patientId,
          implantingEpFreeText: freeTextValue
        }
      }).unwrap();

      notifications.show({
        title: 'Success',
        message: 'Implanting EP updated successfully',
        color: 'green'
      });
    } catch (error) {
      console.error('Error updating Implanting EP:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update Implanting EP. Please try again.',
        color: 'red'
      });
    }
  };

  return (
    <>
      <LabeledField
        label="Implanting EP"
        showEditIcon={true}
        onEdit={handleEditClick}
        isLoading={isUpdatingPatient}
        valueClassName={styles.implantingEPSelectValue}
        value={
          isEditingFreeText ? (
            <TextInput
              autoFocus
              value={freeTextValue}
              onChange={(e) => setFreeTextValue(e.currentTarget.value)}
              onBlur={handleFreeTextSave}
              onKeyDown={(e) => e.key === 'Enter' && handleFreeTextSave()}
              placeholder="Enter Implanting EP"
              size="xs"
            />
          ) : (
            <div className={styles.providerNameWrapper}>
              <span>
                {providerName && providerName !== '-' ? capitalize(providerName) : 'Not assigned'}
              </span>
              {providerName && providerName !== '-' && (
                <span
                  className={styles.clearIcon}
                  onClick={() => {
                    setProviderName('');
                    handleImplantingEPChange(null);
                  }}>
                  ✕
                </span>
              )}
            </div>
          )
        }
      />
      {implantingProviderType !== 'FREE_TEXT' && (
        <ReferringProviderSelectionModal
          opened={opened}
          onClose={toggleImplantingEPModal}
          onProviderSelect={handleProviderSelect}
          headingText="Select Implanting EP"
        />
      )}
    </>
  );
};

export default ImplantingEPSelect;